export type PointerKind = "mouse" | "touch" | "pen" | "unknown";

export type ButtonFrameState = {
  down: boolean;
  pressed: boolean;
  released: boolean;
};

export type PointerFrameState = {
  active: boolean;
  kind: PointerKind;
  id: number;

  x: number;
  y: number;

  dx: number;
  dy: number;

  moved: boolean;
  hover: boolean;

  wheel_x: number;
  wheel_y: number;
};

export type PinchFrameState = {
  active: boolean;
  started: boolean;
  ended: boolean;

  distance: number;
  previous_distance: number;
  scale_delta: number;

  midpoint_x: number;
  midpoint_y: number;
};

export type InputFrameState = {
  pointer: PointerFrameState;
  pinch: PinchFrameState;
  buttons: {
    primary: ButtonFrameState;
    secondary: ButtonFrameState;
    middle: ButtonFrameState;
  };
  keys: Record<string, boolean>;
};

type TouchPoint = {
  x: number;
  y: number;
};

export const Keys = {
  A: "KeyA",
  W: "KeyW",
  S: "KeyS",
  D: "KeyD",
  X: "KeyX",
  C: "KeyC",
  O: "KeyO",
  Z: "KeyZ",

  Space: "Space",

  Up: "ArrowUp",
  Down: "ArrowDown",
  Left: "ArrowLeft",
  Right: "ArrowRight",

  Digit0: "Digit0",
  Digit1: "Digit1",
  Digit2: "Digit2",
  Digit3: "Digit3",
  Digit4: "Digit4",
  Digit5: "Digit5",
  Digit6: "Digit6",
  Digit7: "Digit7",
  Digit8: "Digit8",
  Digit9: "Digit9",

  PageUp: "PageUp",
  PageDown: "PageDown",

  Minus: "Minus",
  Equal: "Equal",
  NumpadMinus: "NumpadSubtract",
  NumpadPlus: "NumpadAdd",

  Shift: "ShiftLeft",
  ShiftRight: "ShiftRight",
  Ctrl: "ControlLeft",
  CtrlRight: "ControlRight",
  Alt: "AltLeft",
  AltRight: "AltRight",
  Meta: "MetaLeft",
  MetaRight: "MetaRight",

  Enter: "Enter",
  Tab: "Tab",
  Escape: "Escape",
} as const;

export type KeyName = keyof typeof Keys;

export type KeyChord = {
  required: string[];
  forbidden?: string[];
  trigger?: string;
};

export function chord(...keys: string[]): KeyChord {
  return { required: keys };
}

export function chord_exclusive(required: string[], forbidden: string[]): KeyChord {
  return { required, forbidden };
}

export const Chords = {
  Save:      { required: [Keys.Ctrl, Keys.S], forbidden: [Keys.Shift] } as KeyChord,
  SaveAs:    { required: [Keys.Ctrl, Keys.Shift, Keys.S] } as KeyChord,
  Undo:      { required: [Keys.Ctrl, Keys.Z], forbidden: [Keys.Shift] } as KeyChord,
  Redo:      { required: [Keys.Ctrl, Keys.Shift, Keys.Z] } as KeyChord,
  Duplicate: { required: [Keys.Ctrl, Keys.D] } as KeyChord,
  Delete:    { required: [Keys.Shift, Keys.X] } as KeyChord,
  Snap:      { required: [Keys.Shift, Keys.S] } as KeyChord,
} as const;

export default class InputService {
  current: InputFrameState;
  previous: InputFrameState;

  private element: HTMLElement | null = null;
  private touch_points: Map<number, TouchPoint> = new Map();
  private tracked_keys: Set<string>;

  constructor() {
    this.current = this.create_empty_state();
    this.previous = this.create_empty_state();

    this.tracked_keys = new Set(Object.values(Keys));

    this.on_pointer_down = this.on_pointer_down.bind(this);
    this.on_pointer_move = this.on_pointer_move.bind(this);
    this.on_pointer_up = this.on_pointer_up.bind(this);
    this.on_pointer_cancel = this.on_pointer_cancel.bind(this);
    this.on_wheel = this.on_wheel.bind(this);
    this.on_key_down = this.on_key_down.bind(this);
    this.on_key_up = this.on_key_up.bind(this);
    this.on_context_menu = this.on_context_menu.bind(this);
    this.on_blur = this.on_blur.bind(this);
    this.on_pointer_enter = this.on_pointer_enter.bind(this);
    this.on_visibility_change = this.on_visibility_change.bind(this);
  }

  private create_empty_state(): InputFrameState {
    return {
      pointer: {
        active: false,
        kind: "unknown",
        id: -1,
        x: 0,
        y: 0,
        dx: 0,
        dy: 0,
        moved: false,
        hover: false,
        wheel_x: 0,
        wheel_y: 0,
      },
      pinch: {
        active: false,
        started: false,
        ended: false,
        distance: 0,
        previous_distance: 0,
        scale_delta: 1.0,
        midpoint_x: 0,
        midpoint_y: 0,
      },
      buttons: {
        primary: { down: false, pressed: false, released: false },
        secondary: { down: false, pressed: false, released: false },
        middle: { down: false, pressed: false, released: false },
      },
      keys: {},
    };
  }

  private clone_state(state: InputFrameState): InputFrameState {
    return {
      pointer: { ...state.pointer },
      pinch: { ...state.pinch },
      buttons: {
        primary: { ...state.buttons.primary },
        secondary: { ...state.buttons.secondary },
        middle: { ...state.buttons.middle },
      },
      keys: { ...state.keys },
    };
  }

  attach(element: HTMLElement): void {
    this.detach();

    this.element = element;

    element.addEventListener("pointerdown", this.on_pointer_down);
    element.addEventListener("pointermove", this.on_pointer_move);
    element.addEventListener("pointerup", this.on_pointer_up);
    element.addEventListener("pointercancel", this.on_pointer_cancel);
    element.addEventListener("wheel", this.on_wheel, { passive: false });
    element.addEventListener("contextmenu", this.on_context_menu);
    element.addEventListener("pointerenter", this.on_pointer_enter);

    window.addEventListener("keydown", this.on_key_down);
    window.addEventListener("keyup", this.on_key_up);
    window.addEventListener("blur", this.on_blur);
    document.addEventListener("visibilitychange", this.on_visibility_change);

    window.addEventListener("pointerup", this.on_pointer_up);
    window.addEventListener("pointercancel", this.on_pointer_cancel);
  }

  detach(): void {
    if (!this.element) return;

    this.element.removeEventListener("pointerdown", this.on_pointer_down);
    this.element.removeEventListener("pointermove", this.on_pointer_move);
    this.element.removeEventListener("pointerup", this.on_pointer_up);
    this.element.removeEventListener("pointercancel", this.on_pointer_cancel);
    this.element.removeEventListener("wheel", this.on_wheel);
    this.element.removeEventListener("contextmenu", this.on_context_menu);
    this.element.removeEventListener("pointerenter", this.on_pointer_enter);
    window.removeEventListener("keydown", this.on_key_down);
    window.removeEventListener("keyup", this.on_key_up);
    window.removeEventListener("blur", this.on_blur);
    document.removeEventListener("visibilitychange", this.on_visibility_change);

    window.removeEventListener("pointerup", this.on_pointer_up);
    window.removeEventListener("pointercancel", this.on_pointer_cancel);

    this.element = null;
    this.touch_points.clear();
  }
    begin_frame(): void {
    this.previous = this.clone_state(this.current);

    this.current.pointer.dx = 0;
    this.current.pointer.dy = 0;
    this.current.pointer.moved = false;
    this.current.pointer.wheel_x = 0;
    this.current.pointer.wheel_y = 0;

    this.current.pinch.started = false;
    this.current.pinch.ended = false;
    this.current.pinch.scale_delta = 1.0;

    this.current.buttons.primary.pressed = false;
    this.current.buttons.primary.released = false;
    this.current.buttons.secondary.pressed = false;
    this.current.buttons.secondary.released = false;
    this.current.buttons.middle.pressed = false;
    this.current.buttons.middle.released = false;
  }

  end_frame(): void {
    this.current.buttons.primary.pressed =
      this.current.buttons.primary.down && !this.previous.buttons.primary.down;
    this.current.buttons.primary.released =
      !this.current.buttons.primary.down && this.previous.buttons.primary.down;

    this.current.buttons.secondary.pressed =
      this.current.buttons.secondary.down && !this.previous.buttons.secondary.down;
    this.current.buttons.secondary.released =
      !this.current.buttons.secondary.down && this.previous.buttons.secondary.down;

    this.current.buttons.middle.pressed =
      this.current.buttons.middle.down && !this.previous.buttons.middle.down;
    this.current.buttons.middle.released =
      !this.current.buttons.middle.down && this.previous.buttons.middle.down;
  }

  private on_visibility_change(): void {
  if (document.hidden) {
    this.on_blur();
  }
}

  private on_pointer_enter(e: PointerEvent): void {
  if (e.pointerType !== "mouse") return;

  // `buttons` is a bitmask: 1 = primary, 2 = secondary, 4 = middle
  const primary_held   = (e.buttons & 1) !== 0;
  const secondary_held = (e.buttons & 2) !== 0;
  const middle_held    = (e.buttons & 4) !== 0;

  if (!primary_held)   this.current.buttons.primary.down = false;
  if (!secondary_held) this.current.buttons.secondary.down = false;
  if (!middle_held)    this.current.buttons.middle.down = false;
}

  is_key_down(code: string): boolean {
    return !!this.current.keys[code];
  }

  was_key_pressed(code: string): boolean {
    return !!this.current.keys[code] && !this.previous.keys[code];
  }

  was_key_released(code: string): boolean {
    return !this.current.keys[code] && !!this.previous.keys[code];
  }

  consume_key(code: string): void {
    this.current.keys[code] = false;
    this.previous.keys[code] = false;
  }

  is_chord_held(c: KeyChord): boolean {
    for (const code of c.required) {
      if (!this.current.keys[code]) return false;
    }

    if (c.forbidden) {
      for (const code of c.forbidden) {
        if (this.current.keys[code]) return false;
      }
    }

    return true;
  }

  was_chord_pressed(c: KeyChord): boolean {
    if (!this.is_chord_held(c)) return false;

    const trigger = c.trigger ?? c.required[c.required.length - 1];

    if (!this.current.keys[trigger] || this.previous.keys[trigger]) {
      return false;
    }

    for (const code of c.required) {
      if (code === trigger) continue;
      if (!this.previous.keys[code]) return false;
    }

    return true;
  }

  was_chord_released(c: KeyChord): boolean {
    const trigger = c.trigger ?? c.required[c.required.length - 1];

    if (this.current.keys[trigger] || !this.previous.keys[trigger]) {
      return false;
    }

    for (const code of c.required) {
      if (code === trigger) continue;
      if (!this.previous.keys[code]) return false;
    }

    if (c.forbidden) {
      for (const code of c.forbidden) {
        if (this.previous.keys[code]) return false;
      }
    }

    return true;
  }

  get_axis_xy(): { x: number; y: number } {
    let x = 0;
    let y = 0;

    if (this.is_key_down(Keys.D) || this.is_key_down(Keys.Right)) x += 1;
    if (this.is_key_down(Keys.A) || this.is_key_down(Keys.Left))  x -= 1;
    if (this.is_key_down(Keys.W) || this.is_key_down(Keys.Up))    y -= 1;
    if (this.is_key_down(Keys.S) || this.is_key_down(Keys.Down))  y += 1;

    return { x, y };
  }

  is_primary_down(): boolean {
    return this.current.buttons.primary.down;
  }

  was_primary_pressed(): boolean {
    return this.current.buttons.primary.pressed;
  }

  was_primary_released(): boolean {
    return this.current.buttons.primary.released;
  }

  is_pinching(): boolean {
    return this.current.pinch.active;
  }
    private on_pointer_down(e: PointerEvent): void {
    if (!this.element) return;

    if (e.pointerType === "touch") {
      const p = this.get_local_position(e);
      this.touch_points.set(e.pointerId, p);

      if (this.touch_points.size <= 2) {
        this.element.setPointerCapture(e.pointerId);
      }

      if (this.touch_points.size === 1) {
        this.current.pointer.active = true;
        this.current.pointer.kind = "touch";
        this.current.pointer.id = e.pointerId;
        this.current.pointer.hover = false;
        this.current.pointer.x = p.x;
        this.current.pointer.y = p.y;
        this.current.buttons.primary.down = true;
      }

      this.update_pinch_state();
      return;
    }

    if (this.current.pointer.active && this.current.pointer.id !== e.pointerId) {
      return;
    }

    this.element.setPointerCapture(e.pointerId);

    this.current.pointer.active = true;
    this.current.pointer.kind = this.map_pointer_kind(e.pointerType);
    this.current.pointer.id = e.pointerId;
    this.current.pointer.hover = e.pointerType === "mouse";

    this.update_pointer_position(e);
    this.set_button_state(e.button, true);
  }

  private on_pointer_move(e: PointerEvent): void {
    if (e.pointerType === "touch") {
      if (!this.touch_points.has(e.pointerId)) return;

      const p = this.get_local_position(e);
      const previous = this.touch_points.get(e.pointerId)!;
      this.touch_points.set(e.pointerId, p);

      if (!this.current.pinch.active && this.current.pointer.id === e.pointerId) {
        this.current.pointer.dx += p.x - previous.x;
        this.current.pointer.dy += p.y - previous.y;
        this.current.pointer.x = p.x;
        this.current.pointer.y = p.y;
        this.current.pointer.moved = true;
      }

      this.update_pinch_state();
      return;
    }

    if (this.current.pointer.active && this.current.pointer.id !== e.pointerId) {
      return;
    }

    this.current.pointer.kind = this.map_pointer_kind(e.pointerType);
    this.current.pointer.id = e.pointerId;
    this.current.pointer.hover = e.pointerType === "mouse" && !this.any_button_down();

    this.update_pointer_position(e);
  }

  private on_pointer_up(e: PointerEvent): void {
    if (e.pointerType === "touch") {
      if (this.element?.hasPointerCapture(e.pointerId)) {
        this.element.releasePointerCapture(e.pointerId);
      }

      this.touch_points.delete(e.pointerId);
      this.update_pinch_state();

      if (this.current.pointer.id === e.pointerId) {
        this.current.pointer.active = false;
        this.current.pointer.id = -1;
      }

      if (this.touch_points.size === 1) {
        const [remaining_id, remaining_point] = Array.from(this.touch_points.entries())[0];
        this.current.pointer.active = true;
        this.current.pointer.kind = "touch";
        this.current.pointer.id = remaining_id;
        this.current.pointer.hover = false;
        this.current.pointer.x = remaining_point.x;
        this.current.pointer.y = remaining_point.y;
         this.current.buttons.primary.down = true;
      }

      if (this.touch_points.size === 0) {
        this.current.pointer.active = false;
        this.current.pointer.kind = "unknown";
        this.current.pointer.id = -1;
        this.current.buttons.primary.down = false;
      }

      return;
    }

    if (this.current.pointer.id !== e.pointerId) return;

    if (this.element?.hasPointerCapture(e.pointerId)) {
      this.element.releasePointerCapture(e.pointerId);
    }

    this.update_pointer_position(e);
    this.set_button_state(e.button, false);

    if (!this.any_button_down()) {
      this.current.pointer.active = false;
      this.current.pointer.id = -1;
      this.current.pointer.kind = "unknown";
    }
  }

  private on_pointer_cancel(e: PointerEvent): void {
    if (e.pointerType === "touch") {
      if (this.element?.hasPointerCapture(e.pointerId)) {
        this.element.releasePointerCapture(e.pointerId);
      }

      this.touch_points.delete(e.pointerId);
      this.update_pinch_state();

      if (this.touch_points.size === 0) {
        this.current.pointer.active = false;
        this.current.pointer.kind = "unknown";
        this.current.pointer.id = -1;
        this.current.buttons.primary.down = false;
      }

      return;
    }

    if (this.current.pointer.id !== e.pointerId) return;

    if (this.element?.hasPointerCapture(e.pointerId)) {
      this.element.releasePointerCapture(e.pointerId);
    }

    this.set_button_state(0, false);
    this.set_button_state(1, false);
    this.set_button_state(2, false);

    this.current.pointer.active = false;
    this.current.pointer.id = -1;
    this.current.pointer.kind = "unknown";
  }

    private on_wheel(e: WheelEvent): void {
    e.preventDefault();

    this.current.pointer.wheel_x += e.deltaX;
    this.current.pointer.wheel_y += e.deltaY;
  }

  private on_key_down(e: KeyboardEvent): void {
    if (!this.tracked_keys.has(e.code)) return;

    if (
      e.code === "Space" ||
      e.code === "ArrowUp" ||
      e.code === "ArrowDown" ||
      e.code === "ArrowLeft" ||
      e.code === "ArrowRight"||
      e.code === "PageUp" ||
      e.code === "PageDown"
    ) {
      e.preventDefault();
    }

    if (e.repeat) return;

    this.current.keys[e.code] = true;
  }

  private on_key_up(e: KeyboardEvent): void {
    if (!this.tracked_keys.has(e.code)) return;
    this.current.keys[e.code] = false;
  }

  private on_blur(): void {
    for (const code of this.tracked_keys) {
      this.current.keys[code] = false;
    }
    this.current.buttons.primary.down = false;
    this.current.buttons.secondary.down = false;
    this.current.buttons.middle.down = false;

    this.current.pointer.active = false;
    this.current.pointer.id = -1;
    this.current.pointer.kind = "unknown";
  }

  private on_context_menu(e: MouseEvent): void {
    e.preventDefault();
  }

  private get_local_position(e: PointerEvent): TouchPoint {
  if (!this.element) return { x: 0, y: 0 };

  const rect = this.element.getBoundingClientRect();

  let x = e.clientX - rect.left;
  let y = e.clientY - rect.top;

  if (this.element instanceof HTMLCanvasElement) {
    const scale_x = this.element.width / rect.width;
    const scale_y = this.element.height / rect.height;

    x *= scale_x;
    y *= scale_y;
  }

  return { x, y };
}

  private update_pointer_position(e: PointerEvent): void {
    const p = this.get_local_position(e);

    this.current.pointer.dx += p.x - this.current.pointer.x;
    this.current.pointer.dy += p.y - this.current.pointer.y;
    this.current.pointer.x = p.x;
    this.current.pointer.y = p.y;
    this.current.pointer.moved = true;
  }

  private update_pinch_state(): void {
    const was_active = this.current.pinch.active;

    if (this.touch_points.size !== 2) {
      this.current.pinch.active = false;
      this.current.pinch.distance = 0;
      this.current.pinch.previous_distance = 0;
      this.current.pinch.scale_delta = 1.0;
      this.current.pinch.midpoint_x = 0;
      this.current.pinch.midpoint_y = 0;

      if (was_active) {
        this.current.pinch.ended = true;
      }

      return;
    }

    const points = Array.from(this.touch_points.values());
    const a = points[0];
    const b = points[1];

    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const distance = Math.hypot(dx, dy);
    const midpoint_x = (a.x + b.x) * 0.5;
    const midpoint_y = (a.y + b.y) * 0.5;

    this.current.pinch.active = true;
    this.current.pinch.midpoint_x = midpoint_x;
    this.current.pinch.midpoint_y = midpoint_y;

    if (!was_active) {
      this.current.pinch.started = true;
      this.current.pinch.distance = distance;
      this.current.pinch.previous_distance = distance;
      this.current.pinch.scale_delta = 1.0;
      return;
    }

    this.current.pinch.previous_distance = this.current.pinch.distance;
    this.current.pinch.distance = distance;

    if (this.current.pinch.previous_distance > 0) {
      this.current.pinch.scale_delta =
        this.current.pinch.distance / this.current.pinch.previous_distance;
    } else {
      this.current.pinch.scale_delta = 1.0;
    }
  }

  private set_button_state(button: number, down: boolean): void {
    if (button === 0) this.current.buttons.primary.down = down;
    if (button === 1) this.current.buttons.middle.down = down;
    if (button === 2) this.current.buttons.secondary.down = down;
  }

  private any_button_down(): boolean {
    return (
      this.current.buttons.primary.down ||
      this.current.buttons.secondary.down ||
      this.current.buttons.middle.down
    );
  }

  private map_pointer_kind(pointer_type: string): PointerKind {
    if (pointer_type === "mouse") return "mouse";
    if (pointer_type === "touch") return "touch";
    if (pointer_type === "pen") return "pen";
    return "unknown";
  }
}
